import React, { useState } from 'react';
import { Modal, Upload, Button, Alert, Table, Icon, message } from 'antd';
import Papa from 'papaparse';
import { bulkCreateNewRequests } from '../../services/api/onboarding-requests';

const SAMPLE_ROW = {
  'Agent Name': 'Rahul Sharma',
  'Source of Request': 'ACM',
  'Center Name': 'Apollo Clinic',
  Location: 'MG Road, Bengaluru, Karnataka',
  City: 'Bengaluru',
  State: 'Karnataka',
  'Center Contact Number': '9876543210',
  'Doctor Name': 'Dr. Ravi Kumar',
  'Doctor Qualification': 'MBBS, MD',
  'Doctor Vertical': 'General Physician',
};

const errorColumns = [
  { title: 'Row', dataIndex: 'row', key: 'row', width: 80 },
  { title: 'Column', dataIndex: 'column', key: 'column', width: 220 },
  { title: 'Error', dataIndex: 'message', key: 'message' },
];

function downloadSampleCsv() {
  const csv = Papa.unparse([SAMPLE_ROW], { header: true });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'onboarding_request_sample.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function flattenErrorRows(results) {
  const rows = [];
  (results || []).forEach((result) => {
    if (result.status === 'failed') {
      (result.errors || []).forEach((err, index) => {
        rows.push({
          key: `${result.row}-${index}`,
          row: result.row,
          column: err.column,
          message: err.message,
        });
      });
    }
  });
  return rows;
}

function BulkUploadRequestModal({ visible, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const beforeUpload = (selectedFile) => {
    const isCsv =
      selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
    if (!isCsv) {
      message.error('Please select a .csv file');
      return false;
    }
    setFile(selectedFile);
    setResult(null);
    setError(null);
    return false;
  };

  const handleUpload = () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    bulkCreateNewRequests(file)
      .then((response) => {
        setResult(response.body);
        if (response.body.summary && response.body.summary.successCount > 0) {
          onSuccess();
        }
      })
      .catch((err) => {
        const data = err && err.response && err.response.data;
        const serverMessage = data && (data.errorMessage || data.message);
        setError(serverMessage || 'Upload failed, please check the file and try again.');
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const errorRows = result ? flattenErrorRows(result.results) : [];

  return (
    <Modal
      title="Create New Requests via CSV"
      visible={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>,
      ]}
      width={700}
    >
      <Button onClick={downloadSampleCsv} style={{ marginBottom: 16 }}>
        <Icon type="download" /> Download Sample CSV
      </Button>

      <Upload.Dragger
        accept=".csv"
        fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
        beforeUpload={beforeUpload}
        onRemove={() => setFile(null)}
      >
        <p className="ant-upload-drag-icon">
          <Icon type="inbox" />
        </p>
        <p className="ant-upload-text">
          Click or drag a CSV file to this area to upload
        </p>
      </Upload.Dragger>

      <Button
        type="primary"
        style={{ marginTop: 16, backgroundColor: '#448EF7' }}
        disabled={!file}
        loading={uploading}
        onClick={handleUpload}
      >
        Upload &amp; Validate
      </Button>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <Alert
            type={
              result.summary && result.summary.failedCount > 0
                ? 'warning'
                : 'success'
            }
            showIcon
            message={
              result.summary
                ? `${result.summary.successCount} of ${result.summary.totalRows} requests created successfully.`
                : result.message
            }
          />
          {errorRows.length > 0 && (
            <Table
              style={{ marginTop: 12 }}
              size="small"
              columns={errorColumns}
              dataSource={errorRows}
              pagination={false}
            />
          )}
          <Button onClick={reset} style={{ marginTop: 12 }}>
            Start Over
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default BulkUploadRequestModal;
